<script>
    import { getContext } from 'svelte';
    import { Policy, Language } from "@Store";
    import Question from "@components/Question.svelte";
    import { validateEstablishment, isAnsweredById, getAnswerText } from "@helpers/validator";
    import { establishmentQuestions } from "@helpers/questions";

    const CONTEXT = getContext('CONTEXT');

    function handleAnswer(e) {
        CONTEXT.handleAnswer(e);
    }

    // Format currency for display
    function formatCurrency(value) {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: 'CAD',
            minimumFractionDigits: 2
        }).format(value);
    }

    // Call the function to get the questions object
    $: questionsList = establishmentQuestions($Policy);

    $: sole_practitioner_or_clinic_question_answered = isAnsweredById($Policy, questionsList.sole_practitioner_or_clinic_question);
    $: is_clinic = sole_practitioner_or_clinic_question_answered && getAnswerText($Policy, questionsList.sole_practitioner_or_clinic_question) === 'Clinic';

    // Calculate total revenues from component values
    $: canadianRevenue = parseFloat(getAnswerText($Policy, questionsList.canadian_revenue_question)) || 0;
    $: usaRestOfWorldRevenue = parseFloat(getAnswerText($Policy, questionsList.usa_rest_of_world_revenue_question)) || 0;
    $: totalRevenue = canadianRevenue + usaRestOfWorldRevenue;

    $: canadianRevenueCurrentYear = parseFloat(getAnswerText($Policy, questionsList.canadian_revenue_current_year_question)) || 0;
    $: usaRestOfWorldRevenueCurrentYear = parseFloat(getAnswerText($Policy, questionsList.usa_rest_of_world_revenue_current_year_question)) || 0;
    $: totalRevenueCurrentYear = canadianRevenueCurrentYear + usaRestOfWorldRevenueCurrentYear;

    // Auto-submit calculated totals when they change
    $: if (questionsList.total_revenue_question && (canadianRevenue > 0 || usaRestOfWorldRevenue > 0)) {
        handleAnswer({
            question: questionsList.total_revenue_question,
            answer: totalRevenue.toString()
        });
    }

    $: if (questionsList.total_revenue_current_year_question && (canadianRevenueCurrentYear > 0 || usaRestOfWorldRevenueCurrentYear > 0)) {
        handleAnswer({
            question: questionsList.total_revenue_current_year_question,
            answer: totalRevenueCurrentYear.toString()
        });
    }

    $: valid = validateEstablishment($Policy);

    $: {
        CONTEXT.routeValidation.update(validation => ({
            ...validation,
            establishment: valid
        }));
    }
</script>

<div class="page-container">
    <div class="questions-wrapper">
        {#if questionsList.sole_practitioner_or_clinic_question}
            <Question question={questionsList.sole_practitioner_or_clinic_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
        {/if}
        {#if is_clinic}
            {#if questionsList.how_many_licensed_practitioners_work_in_your_clinic_question}
                <Question question={questionsList.how_many_licensed_practitioners_work_in_your_clinic_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
            {/if}
        {/if}
        {#if questionsList.location_address_question}
            <Question question={questionsList.location_address_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
        {/if}
        {#if questionsList.services_offered_question}
            <Question question={questionsList.services_offered_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
        {/if}
        {#if questionsList.products_offered_question}
            <Question question={questionsList.products_offered_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
        {/if}
        {#if questionsList.total_product_sales_percentage_question}
            <Question question={questionsList.total_product_sales_percentage_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
        {/if}
        <div class="section-container">
            <div class="section-header">
                <div class="section-icon">💰</div>
                <h1>{($Language === 'fr') ? "Revenu annuel brut pour l’exercice financier précédent (CAD)" : 'Gross Annual Revenue for the previous financial year (in CAD)'}</h1>
            </div>
            <div class="sub-questions">
                <div class="sub-question">
                    {#if questionsList.canadian_revenue_question}
                        <Question question={questionsList.canadian_revenue_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    {/if}            
                </div>
                <div class="sub-question">
                    {#if questionsList.usa_rest_of_world_revenue_question}
                        <Question question={questionsList.usa_rest_of_world_revenue_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    {/if}
                </div>
                <div class="sub-question">
                    <div class="calculated-total">
                        <span class="total-label">{($Language === 'fr') ? "Recettes annuelles brutes totales" : 'Total Revenue'}</span>
                        <span class="total-value">{formatCurrency(totalRevenue)}</span>
                    </div>
                </div>

            </div>
        </div>

        <div class="section-container">
            <div class="section-header">
                <div class="section-icon">💰</div>
                <h1>{($Language === 'fr') ? "Revenu annuel brut pour l’exercice financier en cours (CAD)" : 'Gross Annual Revenue for the current financial year (in CAD)'}</h1>
            </div>
            <div class="sub-questions">
                <div class="sub-question">
                    {#if questionsList.canadian_revenue_current_year_question}
                        <Question question={questionsList.canadian_revenue_current_year_question} title="Canadian Revenue" policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    {/if}
                </div>
                <div class="sub-question">
                    {#if questionsList.usa_rest_of_world_revenue_current_year_question}
                        <Question question={questionsList.usa_rest_of_world_revenue_current_year_question} title="USA/Rest of World Revenue" policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
                    {/if}
                </div>
                <div class="sub-question">
                    <div class="calculated-total">
                        <span class="total-label">{($Language === 'fr') ? "Recettes annuelles brutes totales" : 'Total Revenue'}</span>
                        <span class="total-value">{formatCurrency(totalRevenueCurrentYear)}</span>
                    </div>
                </div>

            </div>
        </div>

        {#if questionsList.all_patients_treated_canadian_question}
            <Question question={questionsList.all_patients_treated_canadian_question} policy={$Policy} on:answered={(e) => handleAnswer(e.detail)} />
        {/if}
    </div>
</div>


<style>
    /* Section Container Styles */
    .page-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 1rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    
    .questions-wrapper {
        width: 100%;
        max-width: 500px;
        margin-bottom: 2rem;
    }
    .section-container {
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-sizing: border-box;
        padding: 2rem 1.5rem;
        margin: 2rem 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    }
    
    .section-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding-bottom: 1.5rem;
        border-bottom: 2px solid #e9ecef;
    }
    
    .section-icon {
        font-size: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
        border-radius: 16px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
    }
    
    .section-header h1 {
        font-size: 1.375rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.3;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    
    .sub-questions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }
    
    .sub-question {
        position: relative;
    }
    
    @media (max-width: 768px) {
        .section-container {
            padding: 1.5rem 1rem;
            border-radius: 16px;
        }
    
        .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }
    
        .section-header h1 {
            font-size: 1.25rem;
        }
    
        .section-icon {
            width: 48px;
            height: 48px;
            font-size: 1.75rem;
        }
    
        .sub-question {
            padding-left: 1rem;
        }
    
        .sub-question::before {
            width: 3px;
        }
    }

    /* Calculated Total Styles */
    .calculated-total {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.25rem;
        background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
        border: 2px solid #c8e6c9;
        border-radius: 12px;
        margin-top: 0.5rem;
    }

    .total-label {
        font-size: 1rem;
        font-weight: 600;
        color: #2e7d32;
    }

    .total-value {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1b5e20;
    }</style>